import React from 'react'

import { Typography } from '@grammarly/badger-ui'

import Plagiarism from '../helpers/plagiarism-helper'
import { FormattedMessage } from 'react-intl'

interface PlagiarismChartDescriptionProps {
  totalErrors: number
  plagiarism: Plagiarism
}

const { C3 } = Typography

export const PlagiarismChartDescriptionCopy: React.FC<PlagiarismChartDescriptionProps> = ({
  totalErrors,
  plagiarism,
}) => {
  const hasErrors = !!totalErrors
  const hasPlagiarism = plagiarism.isPresent
  const hasNormalPlagiarism = hasPlagiarism && plagiarism.isNormal
  const hasSignificantPlagiarism = hasPlagiarism && plagiarism.isSignificant

  // Plagiarism and Errors
  const hasNormalPlagiarismAndErrors = hasErrors && hasNormalPlagiarism
  const hasSignificantPlagiarismAndErrors = hasErrors && hasSignificantPlagiarism

  // Plagiarism, No Errors
  const hasNormalPlagiarismAndNoErrors = !hasErrors && hasNormalPlagiarism
  const hasSignificantPlagiarismAndNoErrors = !hasErrors && hasSignificantPlagiarism

  // Errors, No Plagiarism
  const hasErrorsAndNoPlagiarism = hasErrors && !hasPlagiarism

  // No Plagiarism, No Errors
  const hasNoPlagiarismAndNoErrors = !hasErrors && !hasPlagiarism

  return (
    <>
      {hasNormalPlagiarismAndErrors && (
        <FormattedMessage
          id="textChecker.reportFlow.reportSummary.chartDescription.hasNormalPlagiarismAndErrors"
          description="Text Checker - Report Flow - Report Summary - Chart Description - Has Normal Plagiarism And Errors"
          defaultMessage="<strong>We have found plagiarism</strong> in your text and have also detected {totalErrors} writing {totalErrors, plural, one {issue} other {issues}}."
          values={{
            totalErrors,
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      )}

      {hasSignificantPlagiarismAndErrors && (
        <FormattedMessage
          id="textChecker.reportFlow.reportSummary.chartDescription.hasSignificantPlagiarismAndErrors"
          description="Text Checker - Report Flow - Report Summary - Chart Description - Has Significant Plagiarism And Errors"
          defaultMessage="<strong>We have found significant plagiarism</strong> in your text and have also detected {totalErrors} writing {totalErrors, plural, one {issue} other {issues}}."
          values={{
            totalErrors,
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      )}

      {hasNormalPlagiarismAndNoErrors && (
        <FormattedMessage
          id="textChecker.reportFlow.reportSummary.chartDescription.hasNormalPlagiarismAndNoErrors"
          description="Text Checker - Report Flow - Report Summary - Chart Description - Has Normal Plagiarism And No Errors"
          defaultMessage="Your text is free of writing issues, but <strong>we have found plagiarism</strong> in your text."
          values={{
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      )}

      {hasSignificantPlagiarismAndNoErrors && (
        <FormattedMessage
          id="textChecker.reportFlow.reportSummary.chartDescription.hasSignificantPlagiarismAndNoErrors"
          description="Text Checker - Report Flow - Report Summary - Chart Description - Has Significant Plagiarism And No Errors"
          defaultMessage="Your text is free of writing issues, but <strong>we have found significant plagiarism</strong> in your text."
          values={{
            strong: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      )}

      {hasErrorsAndNoPlagiarism && (
        <FormattedMessage
          id="textChecker.reportFlow.reportSummary.chartDescription.hasErrorsAndNoPlagiarism"
          description="Text Checker - Report Flow - Report Summary - Chart Description - Has Errors And No Plagiarism"
          defaultMessage="We didn’t find any plagiarism, but we found {totalErrors} writing {totalErrors, plural, one {issue} other {issues}}."
          values={{
            totalErrors,
          }}
        />
      )}

      {hasNoPlagiarismAndNoErrors && (
        <FormattedMessage
          id="textChecker.reportFlow.reportSummary.chartDescription.hasNoPlagiarismAndNoErrors"
          description="Text Checker - Report Flow - Report Summary - Chart Description - Has No Plagiarism And No Errors"
          defaultMessage="Your text is free of writing issues."
        />
      )}
    </>
  )
}

export const PlagiarismChartDescription: React.FC<PlagiarismChartDescriptionProps> = ({ totalErrors, plagiarism }) => {
  return (
    <C3 as="div" data-component-name="plagiarism-chart-description">
      {PlagiarismChartDescriptionCopy({ totalErrors, plagiarism })}
    </C3>
  )
}
